import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Redirect,
  Switch,
  Route,
  matchPath,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import PrivateRoute from "./components/PrivateRoute";
import Sidebar from "./components/Sidebar";
import LoadingFallback from "./components/LoadingFallback";
import { Flex, Box } from "@chakra-ui/react";
import Navbar from "./components/Navbar";
// Lazy-loaded components imported from centralized lazyComponents definition
import {
  Home,
  Dashboard,
  SuperAdminDashboard,
  PracticeMenu,
  PromptList,
  PromptOverview,
  NounSortingGame,
  Signup,
  Login,
  ForgotAccount,
  ResetPasswordConfirm,
  Payment,
  SentenceStructures,
  SentenceStructureConcept,
  SentenceStructurePracticeNouns,
  SentenceStructurePracticePronouns,
  SentenceStructureTest,
  NounsPage,
  Prep1Structure,
  Prep2Structure,
  Prep3Structure,
  ConjunctionStructure,
  AdjectiveStructure,
  AdverbStructure,
  PluralNoun,
  PossessiveNouns,
  PropComNouns,
  AbPlurNoun,
  NounComponentTest,
  NounQuizPageTest,
  VerbSentenceStructures,
  ArticleSentenceStructures,
  PrepositionSentenceStructures,
  ConjunctionSentenceStructures,
  ArticleStructure,
  ArticleGrammarLegend,
  ArticleSentenceBuilder,
  ArticleLevelSelection,
  ArticleWordBank,
  VerbTenseStructure,
  VerbGrammarLegend,
  VerbSentenceBuilder,
  VerbLevelSelection,
  VerbWordBank,
  AuxiliaryVerbsAndVerbals,
  AdjectiveQuizPage,
  AdverbQuizPage,
  VerbTenseQuizPage,
  ArticleQuizPage,
  PrepositionQuizPage,
  ConjunctionQuiz,
  NounPractice,
  VerbPractice,
  ArticlePractice,
  PrepositionPractice,
  AdjectivePractice,
  AdverbPractice,
  ConjunctionPractice,
  AdjectiveRoyalOrder,
  AdjectiveSentenceStructures,
  AdjectiveFillBlanks,
  AdverbTypes,
  AdverbTypeSorting,
  AdverbForms,
  AdverbRoyalOrder,
  AdverbSentenceStructures,
  AdverbIdentificationGame,
  GradeQuests,
  Grade3Quest,
  Grade4Quest,
  Grade5Quest,
  Grade6Quest,
  Grade7Quest,
  Grade8Quest,
  Grade9Quest,
  TestPrep7,
  TestPrep8,
  TestPrep9,
  TestPrep10,
  TestPrep11,
  ExtraTestPrep,
  Grade3Nouns,
  Grade3NounsSkillQuiz,
  Grade3Pronouns,
  Grade3PronounSkillQuiz,
  Grade3Articles,
  Grade3ArticleSkillQuiz,
  Grade3Adjectives,
  Grade3AdjectiveSkillQuiz,
  Grade3Adverbs,
  Grade3AdverbsSkillQuiz,
  Grade3VerbTypes,
  Grade3VerbTypesSkillQuiz,
  Grade3VerbTense,
  Grade3VerbTenseSkillQuiz,
  Grade3SubjectVerbAgreement,
  Grade3SubjectVerbAgreementSkillQuiz,
  Grade3Prepositions,
  Grade3PrepositionsSkillQuiz,
  Grade3PrepositionalPhrases,
  Grade3PrepositionalPhrasesSkillQuiz,
  Grade3CompoundPrepositions,
  Grade3CompoundPrepositionsSkillQuiz,
  Grade3Conjunctions,
  Grade3ConjunctionsSkillQuiz,
  Grade4Adjectives,
  Grade4AdjectivesSkillQuiz,
  Grade4Adverbs,
  Grade4AdverbsSkillQuiz,
  Grade4Capitalization,
  Grade4CapitalizationSkillQuiz,
  Grade4Commas,
  Grade4CommasSkillQuiz,
  Grade4Conjunctions,
  Grade4ConjunctionsSkillQuiz,
  Grade4Nouns,
  Grade4NounsSkillQuiz,
  Grade4PluralNouns,
  Grade4PluralNounsSkillQuiz,
  Grade4PossessiveNouns,
  Grade4PossessiveNounsSkillQuiz,
  Grade4Prepositions,
  Grade4PrepositionsSkillQuiz,
  Grade4Pronouns,
  Grade4PronounsSkillQuiz,
  Grade4PronounVerb,
  Grade4PronounVerbSkillQuiz,
  Grade4VerbTense,
  Grade4VerbTenseSkillQuiz,
  Grade4Verbs,
  Grade4VerbsSkillQuiz,
  Grade5AdjectivesAdverbs,
  Grade5AdjectivesAdverbsSkillQuiz,
  Grade5Capitalization,
  Grade5CapitalizationSkillQuiz,
  Grade5Commas,
  Grade5CommasSkillQuiz,
  Grade5Conjunctions,
  Grade5ConjunctionsSkillQuiz,
  Grade5Contractions,
  Grade5ContractionsSkillQuiz,
  Grade5Formatting,
  Grade5FormattingSkillQuiz,
  Grade5Nouns,
  Grade5NounsSkillQuiz,
  Grade5Prepositions,
  Grade5PrepositionsSkillQuiz,
  Grade5Pronouns,
  Grade5PronounsSkillQuiz,
  Grade5SentenceStructures,
  Grade5SentenceStructuresSkillQuiz,
  Grade5SubjectVerbAgreement,
  Grade5SubjectVerbAgreementSkillQuiz,
  Grade5TypesOfSentences,
  Grade5TypesOfSentencesSkillQuiz,
  Grade5VerbTense,
  Grade5VerbTenseSkillQuiz,
  Grade5VerbTypes,
  Grade5VerbTypesSkillQuiz,
  Grade6AdjectivesAdverbs,
  Grade6AdjectivesAdverbsSkillQuiz,
  Grade6Capitalization,
  Grade6CapitalizationSkillQuiz,
  Grade6Conjunctions,
  Grade6ConjunctionsSkillQuiz,
  Grade6Contractions,
  Grade6ContractionsSkillQuiz,
  Grade6FrequentlyConfusedWords,
  Grade6FrequentlyConfusedWordsSkillQuiz,
  Grade6Nouns,
  Grade6NounsSkillQuiz,
  Grade6ObjectsInSentences,
  Grade6ObjectsInSentencesSkillQuiz,
  Grade6Prepositions,
  Grade6PrepositionsSkillQuiz,
  Grade6PronounsAntecedents,
  Grade6PronounsAntecedentsSkillQuiz,
  Grade6Punctuation,
  Grade6PunctuationSkillQuiz,
  Grade6SentenceStructures,
  Grade6SentenceStructuresSkillQuiz,
  Grade6SubjectVerbAgreement,
  Grade6SubjectVerbAgreementSkillQuiz,
  Grade6TypesOfPronouns,
  Grade6TypesOfPronounsSkillQuiz,
  Grade6VerbTense,
  Grade6VerbTenseSkillQuiz,
  Grade6Verbs,
  Grade6VerbsSkillQuiz,
  Grade7AdjectivesAdverbs,
  Grade7AdjectivesAdverbsSkillQuiz,
  Grade7Conjunctions,
  Grade7ConjunctionsSkillQuiz,
  Grade7Commas,
  Grade7CommasSkillQuiz,
  Grade7DirectIndirectObjects,
  Grade7DirectIndirectObjectsSkillQuiz,
  Grade7MisplacedModifiers,
  Grade7MisplacedModifiersSkillQuiz,
  Grade7Nouns,
  Grade7NounsSkillQuiz,
  Grade7ParallelStructure,
  Grade7ParallelStructureSkillQuiz,
  Grade7PronounsAntecedents,
  Grade7PronounsAntecedentsSkillQuiz,
  Grade7SentencesFragmentsRunOns,
  Grade7SentencesFragmentsRunOnsSkillQuiz,
  Grade7SubjectVerbAgreement,
  Grade7SubjectVerbAgreementSkillQuiz,
  Grade7VerbTense,
  Grade7VerbTenseSkillQuiz,
  Grade7VerbTypes,
  Grade7VerbTypesSkillQuiz,
  Grade7PhrasesClauses,
  Grade7PhrasesClausesSkillQuiz,
  Grade7Prepositions,
  Grade7PrepositionsSkillQuiz,
  Grade7PronounTypes,
  Grade7PronounTypesSkillQuiz,
  Grade8AdjectivesAdverbs,
  Grade8AdjectivesAdverbsSkillQuiz,
  Grade8Commas,
  Grade8CommasSkillQuiz,
  Grade8Conjunctions,
  Grade8ConjunctionsSkillQuiz,
  Grade8DashesHyphensEllipses,
  Grade8DashesHyphensEllipsesSkillQuiz,
  Grade8DirectIndirectObjects,
  Grade8DirectIndirectObjectsSkillQuiz,
  Grade8MisplacedModifiers,
  Grade8MisplacedModifiersSkillQuiz,
  Grade8Nouns,
  Grade8NounsSkillQuiz,
  Grade8PhrasesClauses,
  Grade8PhrasesClausesSkillQuiz,
  Grade8Prepositions,
  Grade8PrepositionsSkillQuiz,
  Grade8PronounTypes,
  Grade8PronounTypesSkillQuiz,
  Grade8PronounsAntecedents,
  Grade8PronounsAntecedentsSkillQuiz,
  Grade8RestrictiveNonrestrictive,
  Grade8RestrictiveNonrestrictiveSkillQuiz,
  Grade8SemicolonsColonsCommas,
  Grade8SemicolonsColonsCommasSkillQuiz,
  Grade8SentencesFragmentsRunOns,
  Grade8SentencesFragmentsRunOnsSkillQuiz,
  Grade8SubjectVerbAgreement,
  Grade8SubjectVerbAgreementSkillQuiz,
  Grade8VerbTenseMood,
  Grade8VerbTenseMoodSkillQuiz,
  Grade8VerbTypes,
  Grade8VerbTypesSkillQuiz,
  SHSATActivePassive,
  SHSATActivePassiveSkillQuiz,
  SHSATColonsSemicolons,
  SHSATColonsSemicolonsSkillQuiz,
  SHSATRevisingEditing,
  SHSATRevisingEditingSkillQuiz,
  SHSATCommaUsage,
  SHSATCommaUsageSkillQuiz,
  SHSATConjunctions,
  SHSATConjunctionsSkillQuiz,
  SHSATDanglingModifiers,
  SHSATDanglingModifiersSkillQuiz,
  SHSATextra,
  SHSATextraSkillQuiz,
  PSATextra,
  PSATextraSkillQuiz,
  Grade10SATextra,
  Grade10SATextraSkillQuiz,
  Grade11SATextra,
  Grade11SATextraSkillQuiz,
} from "./lazyComponents";

const LOGIN_REQUIRED_ACTIVITY_PATHS = [
  "/sentence-structure/:lessonId/practice-nouns",
  "/sentence-structure/:lessonId/practice-pronouns",
  "/sentence-structure/:lessonId/test",
  "/NounSortingGame",
  "/NounComponentTest",
  "/NounQuizPageTest",
  "/noun-practice",
  "/verb-practice",
  "/article-practice",
  "/preposition-practice",
  "/adjective-fill-blanks",
  "/adjective-practice",
  "/adverb-identification-game",
  "/adverb-type-sorting",
  "/adverb-practice",
  "/conjunction-practice",
  "/adj-quiz",
  "/adverb-quiz",
  "/verb-tense-quiz",
  "/article-quiz",
  "/preposition-quiz",
  "/conjunction-quiz",
];

function AppLayout() {
  const location = useLocation();
  const { currentUser } = useAuth();
  const requiresLogin =
    LOGIN_REQUIRED_ACTIVITY_PATHS.some((path) =>
      matchPath(location.pathname, { path, exact: true })
    ) || /^\/grade-[3-8]\/.+\/[^/]+$/.test(location.pathname);
  const showSidebar =
    location.pathname !== "/" &&
    location.pathname !== "/login" &&
    location.pathname !== "/signup" &&
    location.pathname !== "/forgot-account" &&
    location.pathname !== "/reset-password";
  const isHome = location.pathname === "/";
  const contentRef = React.useRef(null);
  React.useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);
  if (!currentUser && requiresLogin) {
    return (
      <Redirect
        to={{
          pathname: "/login",
          search: `?next=${encodeURIComponent(
            location.pathname + location.search
          )}`,
        }}
      />
    );
  }

  return (
    <Flex direction="column" h="100vh" overflow="hidden">
      <Navbar showSidebar={showSidebar} />
      <Flex flex="1" overflow="hidden">
        {showSidebar && (
          <Box
            flexShrink="0"
            h={{ base: "auto", md: "100%" }}
          >
            <Sidebar />
          </Box>
        )}
        <Box
          ref={contentRef}
          flex="1"
          minW="0"
          h="100%"
          overflowY="auto"
          position="relative"
          bg={isHome ? "paper" : "brand.300"}
        >
          <Suspense fallback={<LoadingFallback />}>
            <Switch>
              <Route path="/" component={Home} exact />
              <Route path="/signup" component={Signup} />
              <Route path="/login" component={Login} />
              <Route path="/forgot-account" component={ForgotAccount} />
              <Route path="/reset-password" component={ResetPasswordConfirm} />
              <Route path="/payment" component={Payment} />
              <Route
                exact
                path="/superadmin"
                component={SuperAdminDashboard}
              />
              <PrivateRoute path="/dashboard" component={Dashboard} />
              <Route path="/overview/:id" component={PromptOverview} />
              <Route path="/practice-menu" component={PracticeMenu} />
              <Route
                path="/sentence-structure"
                component={SentenceStructures}
                exact
              />
              <Route
                path="/sentence-structure/:lessonId/practice-nouns"
                component={SentenceStructurePracticeNouns}
              />
              <Route
                path="/sentence-structure/:lessonId/practice-pronouns"
                component={SentenceStructurePracticePronouns}
              />
              <Route
                path="/sentence-structure/:lessonId/test"
                component={SentenceStructureTest}
              />
              <Route
                path="/sentence-structure/:lessonId"
                component={SentenceStructureConcept}
                exact
              />
              <Route
                path="/verb-tense-structure"
                component={VerbTenseStructure}
              />
              <Route
                path="/verb-grammar-legend"
                component={VerbGrammarLegend}
              />
              <Route
                path="/verb-sentence-builder"
                component={VerbSentenceBuilder}
              />
              <Route
                path="/verb-level-selection"
                component={VerbLevelSelection}
              />
              <Route path="/verb-wordbank" component={VerbWordBank} />
              <Route
                path="/auxiliary-verbs"
                component={AuxiliaryVerbsAndVerbals}
              />
              <Route
                path="/verb-sentence-structures"
                component={VerbSentenceStructures}
              />
              <Route path="/article-structure" component={ArticleStructure} />
              <Route
                path="/article-grammar-legend"
                component={ArticleGrammarLegend}
              />
              <Route
                path="/article-sentence-builder"
                component={ArticleSentenceBuilder}
              />
              <Route
                path="/article-level-selection"
                component={ArticleLevelSelection}
              />
              <Route path="/article-wordbank" component={ArticleWordBank} />
              <Route
                path="/article-sentence-structures"
                component={ArticleSentenceStructures}
              />
              <Route path="/prompts" component={PromptList} />
              <Route
                path="/adjective-structure"
                component={AdjectiveStructure}
              />
              <Route
                path="/adjective-royal-order"
                component={AdjectiveRoyalOrder}
              />
              <Route
                path="/adjective-sentence-structures"
                component={AdjectiveSentenceStructures}
              />
              <Route
                path="/adjective-fill-blanks"
                component={AdjectiveFillBlanks}
              />
              <Route path="/adverb-structure" component={AdverbStructure} />
              <Route path="/adverb-types" component={AdverbTypes} />
              <Route path="/adverb-forms" component={AdverbForms} />
              <Route path="/adverb-royal-order" component={AdverbRoyalOrder} />
              <Route
                path="/adverb-sentence-structures"
                component={AdverbSentenceStructures}
              />
              <Route
                path="/adverb-identification-game"
                component={AdverbIdentificationGame}
              />
              <Route
                path="/adverb-type-sorting"
                component={AdverbTypeSorting}
              />
              <Route
                path="/conjunction-structure"
                component={ConjunctionStructure}
              />
              <Route
                path="/conjunction-sentence-structures"
                component={ConjunctionSentenceStructures}
              />
              <Route path="/prep1-structure" component={Prep1Structure} />
              <Route path="/prep2-structure" component={Prep2Structure} />
              <Route path="/prep3-structure" component={Prep3Structure} />
              <Route
                path="/preposition-sentence-structures"
                component={PrepositionSentenceStructures}
              />
              <Route path="/nouns" component={NounsPage} />
              <Route path="/propcom-nouns" component={PropComNouns} />
              <Route path="/abplur-nouns" component={AbPlurNoun} />
              <Route path="/plural-noun" component={PluralNoun} />
              <Route path="/possessive-nouns" component={PossessiveNouns} />
              <Route path="/NounSortingGame" component={NounSortingGame} />
              <Route path="/NounComponentTest" component={NounComponentTest} />
              <Route path="/NounQuizPageTest" component={NounQuizPageTest} />
              <Route path="/noun-practice" component={NounPractice} />
              <Route path="/verb-practice" component={VerbPractice} />
              <Route path="/article-practice" component={ArticlePractice} />
              <Route
                path="/preposition-practice"
                component={PrepositionPractice}
              />
              <Route path="/adjective-practice" component={AdjectivePractice} />
              <Route path="/adverb-practice" component={AdverbPractice} />
              <Route
                path="/conjunction-practice"
                component={ConjunctionPractice}
              />
              <Route path="/adj-quiz" component={AdjectiveQuizPage} />
              <Route path="/adverb-quiz" component={AdverbQuizPage} />
              <Route path="/verb-tense-quiz" component={VerbTenseQuizPage} />
              <Route path="/article-quiz" component={ArticleQuizPage} />
              <Route path="/preposition-quiz" component={PrepositionQuizPage} />
              <Route path="/conjunction-quiz" component={ConjunctionQuiz} />
              <Route path="/grade-quests" component={GradeQuests} />
              <Route path="/grade-3-quest" component={Grade3Quest} />
              <Route path="/grade-4-quest" component={Grade4Quest} />
              <Route path="/grade-5-quest" component={Grade5Quest} />
              <Route path="/grade-6-quest" component={Grade6Quest} />
              <Route path="/grade-7-quest" component={Grade7Quest} />
              <Route path="/grade-8-quest" component={Grade8Quest} />
              <Route path="/grade-9-quest" component={Grade9Quest} />
              <Route path="/test-prep-7" component={TestPrep7} />
              <Route path="/test-prep-8" component={TestPrep8} />
              <Route path="/test-prep-9" component={TestPrep9} />
              <Route path="/test-prep-10" component={TestPrep10} />
              <Route path="/test-prep-11" component={TestPrep11} />
              <Route exact path="/extra-test-prep" component={ExtraTestPrep} />
              <Route
                path="/grade-3/nouns/:skillSlug"
                component={Grade3NounsSkillQuiz}
              />
              <Route path="/grade-3/nouns" component={Grade3Nouns} />
              <Route
                path="/grade-3/pronouns/:skillSlug"
                component={Grade3PronounSkillQuiz}
              />
              <Route path="/grade-3/pronouns" component={Grade3Pronouns} />
              <Route
                path="/grade-3/articles/:skillSlug"
                component={Grade3ArticleSkillQuiz}
              />
              <Route path="/grade-3/articles" component={Grade3Articles} />
              <Route
                path="/grade-3/adjectives/:skillSlug"
                component={Grade3AdjectiveSkillQuiz}
              />
              <Route path="/grade-3/adjectives" component={Grade3Adjectives} />
              <Route
                path="/grade-3/adverbs/:skillSlug"
                component={Grade3AdverbsSkillQuiz}
              />
              <Route path="/grade-3/adverbs" component={Grade3Adverbs} />
              <Route
                path="/grade-3/verb-types/:skillSlug"
                component={Grade3VerbTypesSkillQuiz}
              />
              <Route path="/grade-3/verb-types" component={Grade3VerbTypes} />
              <Route
                path="/grade-3/verb-tense/:skillSlug"
                component={Grade3VerbTenseSkillQuiz}
              />
              <Route path="/grade-3/verb-tense" component={Grade3VerbTense} />
              <Route
                path="/grade-3/subject-verb-agreement/:skillSlug"
                component={Grade3SubjectVerbAgreementSkillQuiz}
              />
              <Route
                path="/grade-3/subject-verb-agreement"
                component={Grade3SubjectVerbAgreement}
              />
              <Route
                path="/grade-3/prepositions/:skillSlug"
                component={Grade3PrepositionsSkillQuiz}
              />
              <Route
                path="/grade-3/prepositions"
                component={Grade3Prepositions}
              />
              <Route
                path="/grade-3/prepositional-phrases/:skillSlug"
                component={Grade3PrepositionalPhrasesSkillQuiz}
              />
              <Route
                path="/grade-3/prepositional-phrases"
                component={Grade3PrepositionalPhrases}
              />
              <Route
                path="/grade-3/compound-prepositions/:skillSlug"
                component={Grade3CompoundPrepositionsSkillQuiz}
              />
              <Route
                path="/grade-3/compound-prepositions"
                component={Grade3CompoundPrepositions}
              />
              <Route
                path="/grade-3/conjunctions"
                component={Grade3Conjunctions}
              />
              <Route
                path="/grade-3/conjunctions/:sillSlug"
                Component={Grade3ConjunctionsSkillQuiz}
              />
              <Route 
                exact path="/grade-4/adjectives" 
                component={Grade4Adjectives} 
              />
              <Route 
                path="/grade-4/adjectives/:skillSlug" 
                component={Grade4AdjectivesSkillQuiz} 
              />
              <Route exact path="/grade-4/adverbs" component={Grade4Adverbs} />
              <Route 
                path="/grade-4/adverbs/:skillSlug" 
                component={Grade4AdverbsSkillQuiz} 
              />
              <Route 
                exact path="/grade-4/capitalization"
                component={Grade4Capitalization} 
              />
              <Route 
                path="/grade-4/capitalization/:skillSlug" 
                component={Grade4CapitalizationSkillQuiz} 
              />
              <Route exact path="/grade-4/commas" component={Grade4Commas} />
              <Route 
                path="/grade-4/commas/:skillSlug" 
                component={Grade4CommasSkillQuiz} 
              />
              <Route 
                exact path="/grade-4/conjunctions" 
                component={Grade4Conjunctions} 
              />
              <Route 
                path="/grade-4/conjunctions/:skillSlug" 
                component={Grade4ConjunctionsSkillQuiz} 
              />
              <Route exact path="/grade-4/nouns" component={Grade4Nouns} />
              <Route 
                path="/grade-4/nouns/:skillSlug" 
                component={Grade4NounsSkillQuiz} 
              />
              <Route 
                exact path="/grade-4/plural-nouns" 
                component={Grade4PluralNouns} 
              />
              <Route 
                path="/grade-4/plural-nouns/:skillSlug" 
                component={Grade4PluralNounsSkillQuiz} 
              />
              <Route 
                exact path="/grade-4/possessive-nouns"
                component={Grade4PossessiveNouns} 
              />
              <Route 
                path="/grade-4/possessive-nouns/:skillSlug" 
                component={Grade4PossessiveNounsSkillQuiz} 
              />
              <Route 
                exact path="/grade-4/prepositions" 
                component={Grade4Prepositions} 
              />
              <Route 
                path="/grade-4/prepositions/:skillSlug" 
                component={Grade4PrepositionsSkillQuiz} 
              />
              <Route 
                exact path="/grade-4/pronouns" 
                component={Grade4Pronouns} 
              />
              <Route 
                path="/grade-4/pronouns/:skillSlug" 
                component={Grade4PronounsSkillQuiz} 
              />
              <Route 
                exact path="/grade-4/pronoun-verb" 
                component={Grade4PronounVerb} 
              />
              <Route 
                path="/grade-4/pronoun-verb/:skillSlug" 
                component={Grade4PronounVerbSkillQuiz} 
              />
              <Route 
              exact path="/grade-4/verb-tense" component={Grade4VerbTense} />
              <Route 
                path="/grade-4/verb-tense/:skillSlug" 
                component={Grade4VerbTenseSkillQuiz} 
              />
              <Route exact path="/grade-4/verbs" component={Grade4Verbs} />
              <Route 
                path="/grade-4/verbs/:skillSlug" 
                component={Grade4VerbsSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/adjectives-adverbs"
                component={Grade5AdjectivesAdverbs} 
              />
              <Route 
                path="/grade-5/adjectives-adverbs/:skillSlug" 
                component={Grade5AdjectivesAdverbsSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/capitalization" 
                component={Grade5Capitalization} 
              />
              <Route 
                path="/grade-5/capitalization/:skillSlug" 
                component={Grade5CapitalizationSkillQuiz} 
              />
              <Route exact path="/grade-5/commas" component={Grade5Commas} />
              <Route 
                path="/grade-5/commas/:skillSlug" 
                component={Grade5CommasSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/conjunctions" 
                component={Grade5Conjunctions} 
              />
              <Route 
                path="/grade-5/conjunctions/:skillSlug" 
                component={Grade5ConjunctionsSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/contractions" 
                component={Grade5Contractions} 
              />
              <Route 
                path="/grade-5/contractions/:skillSlug" 
                component={Grade5ContractionsSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/formatting" 
                component={Grade5Formatting} 
              />
              <Route 
                path="/grade-5/formatting/:skillSlug" 
                component={Grade5FormattingSkillQuiz} 
              />
              <Route exact path="/grade-5/nouns" component={Grade5Nouns} />
              <Route 
                path="/grade-5/nouns/:skillSlug" 
                component={Grade5NounsSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/prepositions" 
                component={Grade5Prepositions} 
              />
              <Route 
                path="/grade-5/prepositions/:skillSlug" 
                component={Grade5PrepositionsSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/pronouns" 
                component={Grade5Pronouns} 
              />
              <Route 
                path="/grade-5/pronouns/:skillSlug" 
                component={Grade5PronounsSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/sentence-structures"
                component={Grade5SentenceStructures} 
              />
              <Route 
                path="/grade-5/sentence-structures/:skillSlug" 
                component={Grade5SentenceStructuresSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/subject-verb-agreement"
                component={Grade5SubjectVerbAgreement} 
              />
              <Route 
                path="/grade-5/subject-verb-agreement/:skillSlug" 
                component={Grade5SubjectVerbAgreementSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/types-of-sentences"
                component={Grade5TypesOfSentences} 
              />
              <Route 
                path="/grade-5/types-of-sentences/:skillSlug" 
                component={Grade5TypesOfSentencesSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/verb-tense" 
                component={Grade5VerbTense} 
              />
              <Route 
                path="/grade-5/verb-tense/:skillSlug" 
                component={Grade5VerbTenseSkillQuiz} 
              />
              <Route 
                exact path="/grade-5/verb-types" 
                component={Grade5VerbTypes} 
              />
              <Route 
                path="/grade-5/verb-types/:skillSlug" 
                component={Grade5VerbTypesSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/adjectives-adverbs"
                component={Grade6AdjectivesAdverbs} 
              />
              <Route 
                path="/grade-6/adjectives-adverbs/:skillSlug" 
                component={Grade6AdjectivesAdverbsSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/capitalization" 
                component={Grade6Capitalization} 
              />
              <Route 
                path="/grade-6/capitalization/:skillSlug" 
                component={Grade6CapitalizationSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/conjunctions" 
                component={Grade6Conjunctions} 
              />
              <Route 
                path="/grade-6/conjunctions/:skillSlug" 
                component={Grade6ConjunctionsSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/contractions" 
                component={Grade6Contractions} 
              />
              <Route 
                path="/grade-6/contractions/:skillSlug" 
                component={Grade6ContractionsSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/frequently-confused-words"
                component={Grade6FrequentlyConfusedWords} 
              />
              <Route 
                path="/grade-6/frequently-confused-words/:skillSlug" 
                component={Grade6FrequentlyConfusedWordsSkillQuiz} 
              />
              <Route exact path="/grade-6/nouns" component={Grade6Nouns} />
              <Route 
                path="/grade-6/nouns/:skillSlug" 
                component={Grade6NounsSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/objects-in-sentences"
                component={Grade6ObjectsInSentences} 
              />
              <Route 
                path="/grade-6/objects-in-sentences/:skillSlug" 
                component={Grade6ObjectsInSentencesSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/prepositions" 
                component={Grade6Prepositions} 
              />
              <Route 
                path="/grade-6/prepositions/:skillSlug" 
                component={Grade6PrepositionsSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/pronouns-antecedents"
                component={Grade6PronounsAntecedents} 
              />
              <Route 
                path="/grade-6/pronouns-antecedents/:skillSlug" 
                component={Grade6PronounsAntecedentsSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/punctuation" 
                component={Grade6Punctuation} 
              />
              <Route 
                path="/grade-6/punctuation/:skillSlug" 
                component={Grade6PunctuationSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/sentence-structures"
                component={Grade6SentenceStructures} 
              />
              <Route 
                path="/grade-6/sentence-structures/:skillSlug" 
                component={Grade6SentenceStructuresSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/subject-verb-agreement"
                component={Grade6SubjectVerbAgreement} 
              />
              <Route 
                path="/grade-6/subject-verb-agreement/:skillSlug" 
                component={Grade6SubjectVerbAgreementSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/types-of-pronouns"
                component={Grade6TypesOfPronouns} 
              />
              <Route 
                path="/grade-6/types-of-pronouns/:skillSlug" 
                component={Grade6TypesOfPronounsSkillQuiz} 
              />
              <Route 
                exact path="/grade-6/verb-tense" 
                component={Grade6VerbTense} 
              />
              <Route 
                path="/grade-6/verb-tense/:skillSlug" 
                component={Grade6VerbTenseSkillQuiz} 
              />
              <Route exact path="/grade-6/verbs" component={Grade6Verbs} />
              <Route 
                path="/grade-6/verbs/:skillSlug" 
                component={Grade6VerbsSkillQuiz} 
              />
              <Route 
                exact path="/grade-7/adjectives-adverbs" 
                component={Grade7AdjectivesAdverbs} 
              />
              <Route 
                path="/grade-7/adjectives-adverbs/:skillSlug" 
                component={Grade7AdjectivesAdverbsSkillQuiz} 
              />
              <Route 
                exact path="/grade-7/conjunctions" 
                component={Grade7Conjunctions} 
              />
              <Route 
                path="/grade-7/conjunctions/:skillSlug" 
                component={Grade7ConjunctionsSkillQuiz}
              />
              <Route exact path="/grade-7/commas" component={Grade7Commas} />
              <Route 
                path="/grade-7/commas/:skillSlug" 
                component={Grade7CommasSkillQuiz} />
              <Route 
                exact path="/grade-7/direct-indirect-objects" 
                component={Grade7DirectIndirectObjects} />
              <Route 
                path="/grade-7/direct-indirect-objects/:skillSlug" 
                component={Grade7DirectIndirectObjectsSkillQuiz} />
              <Route 
                exact path="/grade-7/misplaced-modifiers" 
                component={Grade7MisplacedModifiers} />
              <Route 
                path="/grade-7/misplaced-modifiers/:skillSlug" 
                component={Grade7MisplacedModifiersSkillQuiz} />
              <Route exact path="/grade-7/nouns" component={Grade7Nouns} />
              <Route 
                path="/grade-7/nouns/:skillSlug" 
                component={Grade7NounsSkillQuiz} 
              />
              <Route 
                exact path="/grade-7/parallel-structure" 
                component={Grade7ParallelStructure} 
              />
              <Route 
                path="/grade-7/parallel-structure/:skillSlug" 
                component={Grade7ParallelStructureSkillQuiz} 
              />
              <Route 
                exact path="/grade-7/pronouns-antecedents" 
                component={Grade7PronounsAntecedents} 
              />
              <Route 
                path="/grade-7/pronouns-antecedents/:skillSlug" 
                component={Grade7PronounsAntecedentsSkillQuiz} 
              />
              <Route 
                exact path="/grade-7/sentences-fragments-run-ons" 
                component={Grade7SentencesFragmentsRunOns} 
              />
              <Route 
                path="/grade-7/sentences-fragments-run-ons/:skillSlug" 
                component={Grade7SentencesFragmentsRunOnsSkillQuiz} 
              />
              <Route 
                exact path="/grade-7/subject-verb-agreement" 
                component={Grade7SubjectVerbAgreement} 
              />
              <Route 
                path="/grade-7/subject-verb-agreement/:skillSlug" 
                component={Grade7SubjectVerbAgreementSkillQuiz} 
              />
              <Route exact path="/grade-7/verb-tense" component={Grade7VerbTense} />
              <Route 
                path="/grade-7/verb-tense/:skillSlug" 
                component={Grade7VerbTenseSkillQuiz}
              />
              <Route exact path="/grade-7/verb-types" component={Grade7VerbTypes} />
              <Route 
                path="/grade-7/verb-types/:skillSlug" 
                component={Grade7VerbTypesSkillQuiz} 
              />
              <Route 
                exact path="/grade-7/phrases-clauses" 
                component={Grade7PhrasesClauses} 
              />
              <Route 
                path="/grade-7/phrases-clauses/:skillSlug" 
                component={Grade7PhrasesClausesSkillQuiz} 
              />
              <Route 
                exact path="/grade-7/prepositions" 
                component={Grade7Prepositions} 
              />
              <Route 
                path="/grade-7/prepositions/:skillSlug" 
                component={Grade7PrepositionsSkillQuiz} 
              />
              <Route 
                exact path="/grade-7/pronoun-types" component={Grade7PronounTypes} />
              <Route 
                path="/grade-7/pronoun-types/:skillSlug" 
                component={Grade7PronounTypesSkillQuiz} 
              />
              <Route
                exact path="/grade-8/adjectives-adverbs"
                component={Grade8AdjectivesAdverbs}
              />
              <Route
                path="/grade-8/adjectives-adverbs/:skillSlug"
                component={Grade8AdjectivesAdverbsSkillQuiz}
              />
              <Route exact path="/grade-8/commas" component={Grade8Commas} />
              <Route
                path="/grade-8/commas/:skillSlug"
                component={Grade8CommasSkillQuiz}
              />
              <Route
                exact path="/grade-8/conjunctions"
                component={Grade8Conjunctions}
              />
              <Route
                path="/grade-8/conjunctions/:skillSlug"
                component={Grade8ConjunctionsSkillQuiz}
              />
              <Route
                exact path="/grade-8/dashes-hyphens-ellipses"
                component={Grade8DashesHyphensEllipses}
              />
              <Route
                path="/grade-8/dashes-hyphens-ellipses/:skillSlug"
                component={Grade8DashesHyphensEllipsesSkillQuiz}
              />
              <Route
                exact path="/grade-8/direct-indirect-objects"
                component={Grade8DirectIndirectObjects}
              />
              <Route
                path="/grade-8/direct-indirect-objects/:skillSlug"
                component={Grade8DirectIndirectObjectsSkillQuiz}
              />
              <Route
                exact path="/grade-8/misplaced-modifiers"
                component={Grade8MisplacedModifiers}
              />
              <Route
                path="/grade-8/misplaced-modifiers/:skillSlug"
                component={Grade8MisplacedModifiersSkillQuiz}
              />
              <Route exact path="/grade-8/nouns" component={Grade8Nouns} />
              <Route
                path="/grade-8/nouns/:skillSlug"
                component={Grade8NounsSkillQuiz}
              />
              <Route
                exact path="/grade-8/phrases-clauses"
                component={Grade8PhrasesClauses}
              />
              <Route
                path="/grade-8/phrases-clauses/:skillSlug"
                component={Grade8PhrasesClausesSkillQuiz}
              />
              <Route
                exact path="/grade-8/prepositions"
                component={Grade8Prepositions}
              />
              <Route
                path="/grade-8/prepositions/:skillSlug"
                component={Grade8PrepositionsSkillQuiz}
              />
              <Route
                exact path="/grade-8/pronouns-antecedents"
                component={Grade8PronounsAntecedents}
              />
              <Route
                path="/grade-8/pronouns-antecedents/:skillSlug"
                component={Grade8PronounsAntecedentsSkillQuiz}
              />
              <Route
                exact path="/grade-8/pronoun-types"
                component={Grade8PronounTypes}
              />
              <Route
                path="/grade-8/pronoun-types/:skillSlug"
                component={Grade8PronounTypesSkillQuiz}
              />
              <Route
                exact path="/grade-8/restrictive-nonrestrictive-elements"
                component={Grade8RestrictiveNonrestrictive}
              />
              <Route
                path="/grade-8/restrictive-nonrestrictive-elements/:skillSlug"
                component={Grade8RestrictiveNonrestrictiveSkillQuiz}
              />
              <Route
                exact path="/grade-8/semicolons-colons-commas"
                component={Grade8SemicolonsColonsCommas}
              />
              <Route
                path="/grade-8/semicolons-colons-commas/:skillSlug"
                component={Grade8SemicolonsColonsCommasSkillQuiz}
              />
              <Route
                exact path="/grade-8/sentences-fragments-run-ons"
                component={Grade8SentencesFragmentsRunOns}
              />
              <Route
                path="/grade-8/sentences-fragments-run-ons/:skillSlug"
                component={Grade8SentencesFragmentsRunOnsSkillQuiz}
              />
              <Route
                exact path="/grade-8/subject-verb-agreement"
                component={Grade8SubjectVerbAgreement}
              />
              <Route
                path="/grade-8/subject-verb-agreement/:skillSlug"
                component={Grade8SubjectVerbAgreementSkillQuiz}
              />
              <Route
                exact path="/grade-8/verb-tense-mood"
                component={Grade8VerbTenseMood}
              />
              <Route
                path="/grade-8/verb-tense-mood/:skillSlug"
                component={Grade8VerbTenseMoodSkillQuiz}
              />
              <Route
                exact path="/grade-8/verb-types"
                component={Grade8VerbTypes}
              />
              <Route
                path="/grade-8/verb-types/:skillSlug"
                component={Grade8VerbTypesSkillQuiz}
              />
              <Route
                exact path="/shsat/active-passive"
                component={SHSATActivePassive}
              />
              <Route
                path="/shsat/active-passive/:skillSlug"
                component={SHSATActivePassiveSkillQuiz}
              />
              <Route
                exact path="/shsat/colons-semicolons-punctuation"
                component={SHSATColonsSemicolons}
              />
              <Route
                path="/shsat/colons-semicolons-punctuation/:skillSlug"
                component={SHSATColonsSemicolonsSkillQuiz}
              />
              <Route
                exact path="/shsat/revising-editing"
                component={SHSATRevisingEditing}
              />
              <Route
                path="/shsat/revising-editing/:skillSlug"
                component={SHSATRevisingEditingSkillQuiz}
              />
              <Route
                exact path="/shsat/comma-usage"
                component={SHSATCommaUsage}
              />
              <Route
                path="/shsat/comma-usage/:skillSlug"
                component={SHSATCommaUsageSkillQuiz}
              />
              <Route
                exact path="/shsat/conjunctions"
                component={SHSATConjunctions}
              />
              <Route
                path="/shsat/conjunctions/:skillSlug"
                component={SHSATConjunctionsSkillQuiz}
              />
              <Route
                exact path="/shsat/dangling-modifiers"
                component={SHSATDanglingModifiers}
              />
              <Route
                path="/shsat/dangling-modifiers/:skillSlug"
                component={SHSATDanglingModifiersSkillQuiz}
              />
              <Route 
                exact path="/extra-test-prep/extra-grade-7-shsat" 
                component={SHSATextra} 
              />
              <Route 
                path="/extra-test-prep/extra-grade-7-shsat/:skillSlug" 
                component={SHSATextraSkillQuiz} 
              />              
              <Route 
                exact path="/extra-test-prep/extra-grade-8-shsat" 
                component={SHSATextra} 
              />
              <Route 
                path="/extra-test-prep/extra-grade-8-shsat/:skillSlug" 
                component={SHSATextraSkillQuiz} 
              />
              <Route
                exact path="/extra-test-prep/extra-grade-9-psat"
                component={PSATextra}
              />
              <Route
                path="/extra-test-prep/extra-grade-9-psat/:skillSlug"
                component={PSATextraSkillQuiz}
              />
              <Route
                exact path="/extra-test-prep/extra-grade-10-sat"
                component={Grade10SATextra}
              />
              <Route
                path="/extra-test-prep/extra-grade-10-sat/:skillSlug"
                component={Grade10SATextraSkillQuiz}
              />
              <Route
                exact path="/extra-test-prep/extra-grade-11-sat"
                component={Grade11SATextra}
              />
              <Route
                path="/extra-test-prep/extra-grade-11-sat/:skillSlug"
                component={Grade11SATextraSkillQuiz}
              />
            </Switch>
          </Suspense>
        </Box>
      </Flex>
    </Flex>
  );
}
const queryClient = new QueryClient();
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppLayout />
        </Router>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} position="bottom-left" />
    </QueryClientProvider>
  );
}
export default App;
